/**
 * Voice quality + filtering helpers for Read-Aloud.
 *
 * Goals:
 *  1) Filter the Web Speech voice list down to ONLY high-quality English voices
 *     (drops the dozens of irrelevant system voices on Android/iOS).
 *  2) Score what remains so we can auto-pick the best one when the student
 *     hasn't chosen a voice yet — avoids the very robotic defaults.
 *  3) Keep the public surface tiny: `filterEnglishVoices` + `pickBestVoice`.
 */

// Names / providers we ALWAYS exclude — low-quality, novelty, or non-speech.
const BLOCKED_PATTERNS = [
  /compact/i,
  /novelty/i,
  /whisper/i,
  /bells/i,
  /bahh/i,
  /bubbles/i,
  /cellos/i,
  /organ/i,
  /trinoids/i,
  /zarvox/i,
  /albert/i,
  /bad news/i,
  /good news/i,
  /jester/i,
  /pipe organ/i,
  /superstar/i,
  /wobble/i,
  /boing/i,
  /deranged/i,
  /hysterical/i,
  /cellos/i,
  /espeak/i,                  // generic Linux fallback — extremely robotic
  /pico/i,                    // SVOX Pico — very robotic on Android
  /\bandroid speech/i,        // legacy Android default (robotic)
];

// Scores voices higher when the name contains these (= higher fidelity).
// Order doesn't matter; all matches accumulate.
const PREMIUM_HINTS = [
  // Microsoft Edge / Windows 11 neural cloud voices (top tier)
  { kw: /\bneural\b/i, w: 90 },
  { kw: /\bneural2\b/i, w: 95 },
  { kw: /\bwavenet\b/i, w: 90 },     // Google WaveNet voices (rare but premium)
  { kw: /\bstudio\b/i, w: 85 },      // Google Studio voices
  { kw: /\bpolyglot\b/i, w: 70 },
  { kw: /\bonline\b/i, w: 65 },      // "Microsoft AriaNeural Online" etc.
  { kw: /\bnatural\b/i, w: 60 },
  { kw: /\b(enhanced|premium)\b/i, w: 65 },  // Apple Enhanced / Premium marker
  { kw: /\(premium\)/i, w: 70 },     // iOS often labels as "Ava (Premium)"
  { kw: /\(enhanced\)/i, w: 65 },
  // Google's cloud voices on Chrome / Android (very natural)
  { kw: /^google\s/i, w: 50 },
  { kw: /^google us english/i, w: 55 },
  { kw: /^google uk english/i, w: 55 },
  // Apple premium voices on iOS 17+ / macOS Sonoma (most natural Apple offers)
  { kw: /\b(ava|evan|zoe|joelle|nathan|noelle|isha|jamie|joamie)\b/i, w: 65 },
  // Apple "Enhanced" / classic high-quality voices
  { kw: /\b(samantha|karen|daniel|moira|tessa|aaron|nicky|allison|tom|susan|serena|kate|oliver|arthur|martha)\b/i, w: 40 },
  // Microsoft "Aria/Jenny/Guy/Davis" — modern Windows TTS
  { kw: /\b(aria|jenny|guy|davis|emma|ryan|sonia|libby|amber|ana|brian|christopher|jacob|jane|nancy|tony)\b/i, w: 45 },
  // Generic "cloud" marker — usually higher quality than offline siblings
  { kw: /\bcloud\b/i, w: 20 },
  // Siri variants (v1..v3 on iOS)
  { kw: /\bsiri\b/i, w: 20 },
];

// Voices we DEMOTE (still shown but never auto-selected) — typical legacy
// or "Desktop" suffix voices on Windows that sound robotic.
const DEMOTE_HINTS = [
  /desktop/i,
  /microsoft david/i,        // venerable but very robotic
  /microsoft mark/i,
  /microsoft zira/i,
  /microsoft hazel/i,
  /microsoft heera/i,
  /microsoft ravi/i,
  /microsoft sean/i,
  /eddy/i,
  /flo/i,
  /grandma/i,
  /grandpa/i,
  /reed/i,
  /rocko/i,
  /sandy/i,
  /shelley/i,
  /\bfred\b/i,                // classic Mac Fred — very robotic
  /\balex\b/i,                // older Mac Alex — robotic compared to Samantha
  /\bvictoria\b/i,            // older Mac Victoria
];

function isEnglish(voice) {
  const l = (voice.lang || "").toLowerCase();
  return l.startsWith("en");
}

function isBlocked(voice) {
  const n = voice.name || "";
  return BLOCKED_PATTERNS.some((re) => re.test(n));
}

/**
 * Return ONLY usable English voices, sorted by quality (best first).
 * On mobile this typically trims a 50-item list down to 3-8 good options.
 */
export function filterEnglishVoices(voices) {
  if (!Array.isArray(voices)) return [];
  const candidates = voices.filter((v) => isEnglish(v) && !isBlocked(v));
  return candidates.sort((a, b) => scoreVoice(b) - scoreVoice(a));
}

function scoreVoice(voice) {
  let score = 0;
  const name = voice.name || "";
  for (const { kw, w } of PREMIUM_HINTS) {
    if (kw.test(name)) score += w;
  }
  for (const re of DEMOTE_HINTS) {
    if (re.test(name)) score -= 40;
  }
  // localService = false ⇒ it's a cloud / streamed voice (usually higher quality).
  if (voice.localService === false) score += 25;
  // Prefer US / UK / AU English over rare regional dialects.
  const lang = (voice.lang || "").toLowerCase();
  if (lang === "en-us" || lang === "en-gb") score += 10;
  if (lang === "en-au" || lang === "en-ca" || lang === "en-ie") score += 5;
  // Default = small tiebreaker, lets the system's preferred voice float up.
  if (voice.default) score += 3;
  return score;
}

/** Auto-pick the highest-quality voice. Returns null if no English voices. */
export function pickBestVoice(voices) {
  const filtered = filterEnglishVoices(voices);
  return filtered[0] || null;
}

/**
 * Returns a quality tier label for a voice — used in the picker UI so students
 * can see at-a-glance which option will sound best.
 */
export function qualityTier(voice) {
  if (!voice) return "";
  const s = scoreVoice(voice);
  if (s >= 80) return "Premium";
  if (s >= 45) return "High";
  if (s >= 15) return "Good";
  if (s <= -20) return "Basic";
  return "Standard";
}

/** Human-friendly label: "Samantha — en-US · Premium" */
export function voiceLabel(voice) {
  if (!voice) return "";
  const tier = qualityTier(voice);
  const lang = voice.lang ? ` · ${voice.lang}` : "";
  return `${voice.name}${lang}${tier ? ` · ${tier}` : ""}`;
}

/**
 * Best-effort platform sniff — used only to decide whether to show the
 * "install Enhanced Siri voice" hint on iOS/iPadOS.
 */
export function isIOSLikeDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIPhoneIPad = /iPhone|iPad|iPod/i.test(ua);
  // iPadOS 13+ reports as Mac — detect via touch points.
  const isIPadOS = /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return isIPhoneIPad || isIPadOS;
}

/**
 * True when the currently-picked voice on an iOS-like device is still a
 * legacy (non-Enhanced/Premium) voice. When this is true we show a one-tap
 * hint pointing the student at Settings → Accessibility → Spoken Content →
 * Voices to download the higher-quality Siri voice.
 */
export function shouldOfferEnhancedVoiceHint(voice) {
  if (!isIOSLikeDevice()) return false;
  if (!voice) return true;   // no voice picked yet — worth offering
  const name = voice.name || "";
  if (/\(premium\)/i.test(name)) return false;
  if (/\(enhanced\)/i.test(name)) return false;
  if (/\bneural\b/i.test(name)) return false;
  // If the score is already high (e.g. Ava, Zoe on iOS 17+) don't nag.
  return scoreVoice(voice) < 60;
}
